import {
  buildPermissionMap,
  hasAnyPermissionWithLegacy,
} from '../../assets/utils/permissions';

/** Archive list filter for claims drilldown APIs (`scope` query param). */
export const CLAIMS_ARCHIVE_SCOPES = ['active', 'archived', 'all'];

export const CLAIMS_ARCHIVE_SCOPE_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'All', value: 'all' },
];

export const CLIENT_REQUEST_TYPES = ['claim_query', 'client_verification'];

/**
 * Payment-stage segments (`claims_status`) — the drilldown queues an operator
 * stands in. Labels are the ones already shown by the stage tabs and the admin
 * hierarchy (PaymentFacilities / PaymentClaims / AdminClaimsHierarchy), not new
 * wording invented for the archive dialog.
 */
export const CLAIMS_SEGMENT_LABELS = {
  0: 'Pending CO',
  1: 'Pending HM',
  2: 'Pending CFO',
  3: 'Paid',
};

/**
 * Segment `0` (Pending CO) is a real, common queue, so it must never be dropped
 * by a truthiness test. Only `null`/`undefined`/`''`/non-integers are "absent".
 * A code outside the known label map (e.g. 4 = Denied in the admin hierarchy) is
 * still returned: passing it through and letting the API answer is safer than
 * silently widening the request to every status.
 *
 * @returns {number|null} the segment, or null when there genuinely is none
 */
export function normalizeClaimsSegment(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }
  return null;
}

/**
 * @returns {string|null} human label for a segment, or null when there is none.
 */
export function getClaimsSegmentLabel(value) {
  const segment = normalizeClaimsSegment(value);
  if (segment === null) return null;
  return CLAIMS_SEGMENT_LABELS[segment] || `Status ${segment}`;
}

export function normalizeClaimsArchiveScope(value) {
  return CLAIMS_ARCHIVE_SCOPES.includes(value) ? value : 'active';
}

export function getClaimsArchiveScope(searchParams) {
  if (!searchParams) return 'active';
  const includeArchived = searchParams.get('include_archived');
  if (includeArchived === '1' || includeArchived === 'true' || includeArchived === 'yes') {
    return searchParams.get('scope') === 'archived' ? 'archived' : 'all';
  }
  return normalizeClaimsArchiveScope(searchParams.get('scope'));
}

export function withClaimsArchiveScope(params = {}, scope) {
  const normalized = normalizeClaimsArchiveScope(scope);
  if (normalized === 'active') {
    return { ...params };
  }
  if (normalized === 'all') {
    return { ...params, include_archived: 1 };
  }
  return { ...params, scope: 'archived' };
}

export function parseArchiveApiBody(response) {
  const root = response?.data ?? response;
  const payload = root?.data && typeof root.data === 'object' && !Array.isArray(root.data)
    ? root.data
    : root;
  return {
    ok: Number(root?.error ?? 0) === 0,
    message: root?.message || payload?.message || null,
    archivedCount: payload?.archived_count ?? payload?.archivedCount ?? payload?.restored_count ?? 0,
    archived: Array.isArray(payload?.archived) ? payload.archived : [],
    skipped: Array.isArray(payload?.skipped) ? payload.skipped : [],
    previewCount: payload?.eligible_count ?? payload?.count ?? payload?.preview_count ?? null,
    facilityName: payload?.facility_name || payload?.facilityName || null,
    facilityId: payload?.facility_id ?? payload?.facilityId ?? null,
    month: payload?.month || null,
    monthLabel: payload?.month_label || payload?.monthLabel || null,
    raw: payload,
  };
}

export function buildClaimsArchiveRequest({
  scope,
  month,
  months,
  facilityId,
  claimsIds,
  segment,
  reason,
}) {
  const body = {};
  if (reason) body.reason = reason;
  // `normalizeClaimsSegment` keeps 0 (Pending CO); a plain `if (segment)` would not.
  const normalizedSegment = normalizeClaimsSegment(segment);
  if (normalizedSegment !== null) body.segment = normalizedSegment;

  if (scope === 'claim' && Array.isArray(claimsIds) && claimsIds.length) {
    body.scope = 'claim';
    body.claims_ids = claimsIds;
    return body;
  }
  if ((scope === 'facility' || (!scope && month && facilityId != null)) && month && facilityId != null) {
    body.scope = 'facility';
    body.month = month;
    body.facility_id = facilityId;
    return body;
  }
  if (scope === 'month' && month) {
    body.scope = 'month';
    body.month = month;
    return body;
  }
  if (scope === 'months' && Array.isArray(months) && months.length) {
    body.scope = 'months';
    body.months = months;
    return body;
  }
  return null;
}

export function buildClientRequestsArchiveRequest({
  scope = 'all',
  month,
  facilityId,
  types,
  queryIds,
  insuranceNos,
  reason,
}) {
  const body = {
    types: Array.isArray(types) && types.length ? types : CLIENT_REQUEST_TYPES,
    scope,
  };
  if (reason) body.reason = reason;
  if (month) body.month = month;
  if (facilityId != null) body.facility_id = facilityId;
  if (scope === 'ids') {
    if (Array.isArray(queryIds) && queryIds.length) body.query_ids = queryIds;
    if (Array.isArray(insuranceNos) && insuranceNos.length) body.insurance_nos = insuranceNos;
  }
  return body;
}

/**
 * Matches api_v1/public/v2.php:3002 `$claimsArchivePerms` exactly.
 */
export const CLAIMS_ARCHIVE_PERMISSIONS = [
  'admin.*',
  'claims.archive',
  'claims.process',
  'claims.payment',
  'claims.approve',
  'claims.update',
];

/**
 * Matches api_v1/public/v2.php:3288 `$clientRequestPerms` exactly.
 */
export const CLIENT_REQUESTS_ARCHIVE_PERMISSIONS = [
  'admin.*',
  'clients.verify',
  'clients.manage_all',
  'claims.process',
  'claims.review',
];

/**
 * These helpers are plain functions (not hooks), so callers that already hold the
 * RBAC map should pass it. Callers that do not are served from the map embedded in
 * `user.profile.permissions`, so the check stays permission-first either way.
 */
function resolvePermissionMap(user, permissionMap) {
  if (permissionMap && Object.keys(permissionMap).length > 0) return permissionMap;
  const raw = user?.profile?.permissions;
  if (Array.isArray(raw)) return buildPermissionMap({ permissions: raw });
  if (raw && typeof raw === 'object') return raw;
  return {};
}

/**
 * Is this session a facility-portal identity rather than an internal one?
 *
 * The API already pins facility users to their own facility and coerces a
 * `month` scope back to `facility`
 * (ClaimsArchiveService::applyActorFacilityConstraint). This is the client-side
 * mirror of that rule, so a cross-facility control is never offered to someone
 * the server would narrow anyway. Mirrors the checks in features/claims/index.js.
 *
 * @param {object} user session user
 */
export function isFacilityScopedUser(user) {
  if (!user) return false;
  if (user?.profile?.user?.type === 'facility') return true;
  if (user?.profile?.user_type === 'facility') return true;
  if (user?.rbac_context?.user_type === 'facility') return true;

  const jwt = user?.jwt;
  if (typeof jwt === 'string') {
    const parts = jwt.split('.');
    if (parts.length === 3) {
      try {
        return JSON.parse(atob(parts[1]))?.data?.rbac_user_type === 'facility';
      } catch (e) {
        // Unreadable token: fall through to "not provably a facility user".
        return false;
      }
    }
  }
  return false;
}

/**
 * Archive / restore controls on the claims queues.
 * @param {object} user session user
 * @param {Record<string, boolean>} [permissionMap] from usePermissions().permissions
 */
export function canManageClaimsArchive(user, permissionMap) {
  return hasAnyPermissionWithLegacy(
    CLAIMS_ARCHIVE_PERMISSIONS,
    resolvePermissionMap(user, permissionMap),
    user,
    [1, 3, 4, 5],
  );
}

/**
 * Archive controls on the claim-query / client-verification queues.
 * @param {object} user session user
 * @param {Record<string, boolean>} [permissionMap] from usePermissions().permissions
 */
export function canManageClientRequestsArchive(user, permissionMap) {
  return hasAnyPermissionWithLegacy(
    CLIENT_REQUESTS_ARCHIVE_PERMISSIONS,
    resolvePermissionMap(user, permissionMap),
    user,
    [1, 3, 4, 5, 6],
  );
}
